'use client';

import { ChevronBottom, ChevronTop } from '@/components/icons';
import '@/components/header/Search/css/search.css';
import { useState } from 'react';
import ReactSelect, { components, type DropdownIndicatorProps, type FilterOptionOption, type GroupBase } from 'react-select';

interface IDropdownProps {
  value: string | number;
  handleChange: (value: string | number) => void;
  options?: Array<{ label: string; value: string | number }>;
  stringOptions?: Array<string>;
  className?: string;
  icon?: React.ReactNode;
  searchable?: boolean;
  allowSearchInput?: boolean;
  placeholder?: string;
}

type TDropdownOption = {
  label: string;
  value: string | number;
};

const DropdownIndicator = (props: DropdownIndicatorProps<TDropdownOption, false, GroupBase<TDropdownOption>>) => {
  const { menuIsOpen } = props.selectProps;
  const Chevron = menuIsOpen ? ChevronTop : ChevronBottom;

  return (
    <components.DropdownIndicator {...props}>
      <div className="flex items-center justify-center" aria-hidden>
        <Chevron />
      </div>
    </components.DropdownIndicator>
  );
};

const filterByStartOfAnyWord = (
  option: FilterOptionOption<TDropdownOption>,
  inputValue: string
): boolean => {
  if (!inputValue.trim()) return true;

  const search = inputValue.toLowerCase().trim();
  const label = typeof option.label === 'string' ? option.label : '';
  const words = label.toLowerCase().split(/\s+/);

  return words.some((word) => word.startsWith(search));
};

export const Dropdown = ({
  value = 'AM',
  handleChange,
  options = [],
  stringOptions = [],
  className = '',
  icon = <ChevronBottom />,
  searchable = false,
  allowSearchInput = true,
  placeholder = 'Select...',
}: IDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const handleSelect = (value: string | number) => {
    handleChange(value);
    setIsOpen(false);
  };

  if (searchable && options.length > 0) {
    const selectedOption = options.find((option) => option.value === value || option.label === value) ?? null;

    return (
      <ReactSelect<TDropdownOption, false>
        instanceId="dropdown-searchable"
        placeholder={placeholder}
        options={options}
        value={selectedOption}
        onChange={(option) => option && handleChange(option.value)}
        filterOption={filterByStartOfAnyWord}
        isSearchable={allowSearchInput}
        className={`select-dropdown no-separator text-white bg-gray-800 rounded ${className}`}
        classNamePrefix="select-dropdown"
        classNames={{
          control: () => 'bg-gray-800 rounded px-2 py-[2px]',
          valueContainer: () => 'text-white rounded-lg lg:pl-2',
          input: () => 'text-white',
          placeholder: () => 'text-gray-400',
          menu: () => 'mt-1',
        }}
        openMenuOnFocus
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        menuPosition="fixed"
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 99999 }),
          input: (base) => ({ ...base, color: 'white' }),
          singleValue: (base) => ({ ...base, color: 'white' }),
        }}
        components={{ DropdownIndicator }}
      />
    );
  }

  return (
    <div className="relative cursor-pointer">
      <div
        className={`${className} w-full flex flex-row items-center justify-between gap-2 text-white bg-gray-800 lg:pl-4 px-2 py-[10px] ${isOpen ? 'rounded-t' : 'rounded'}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="select-none">{value}</span>
        {<div className={isOpen ? '' : 'rotate-180'}>{icon}</div>}
      </div>
      {isOpen && options.length > 0 && (
        <div className={`${className} absolute top-10 left-0 w-full bg-gray-800 xs:px-0 rounded-b z-50`}>
          {options.map((option) => (
            <div
              key={option.value}
              className="w-full lg:pl-4 px-2 py-2 select-none hover:bg-gray-700 last:rounded-b hover:text-white"
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}

      {isOpen && stringOptions.length > 0 && (
        <div className={`${className} absolute top-10 left-0 w-full bg-gray-800 xs:px-0 rounded-b z-50`}>
          {stringOptions.map((option) => (
            <div
              key={option}
              className="w-full lg:pl-4 px-2 py-2 select-none hover:bg-gray-700 last:rounded-b hover:text-white"
              onClick={() => handleSelect(option)}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
